import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WheelPicker, WheelPickerWrapper } from "@ncdai/react-wheel-picker";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import ViewportHeightWrapper from "@/components/ViewportHeightWrapper";

export default function TimeSelect({
  onSelect,
  value,
  id,
  timeOptions,
  onClicked,
  scrollToForm,
  viewportHeightWrapperActive = false,
  onFocus,
  onBlur,
  bottomSheetTitle = "",
}) {
  const t = useTranslationDetector();
  const isMobile = useIsMobile();
  const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);
  const [time, setTime] = useState(value);
  const [shouldRenderWebDropdown, setShouldRenderWebDropdown] = useState(false);
  const escapePressed = useKeyPressed("Escape");
  const ref = useRef(null);

  useEffect(() => {
    if (isOpen) {
      onFocus();
    } else {
      onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClickOutside = useCallback(() => {
    // Mobilde portal açıksa click outside'ı devre dışı bırak
    if (isMobile && shouldRenderPortal) {
      return;
    }

    // Sadece web'de ve portal açık değilken çalıştır
    if (!isMobile && isOpen) {
      setIsOpen(false);
      onClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isOpen, shouldRenderPortal]);

  useOnClickOutside(ref, handleClickOutside);

  const handleClick = () => {
    onClicked(true);
    setIsOpen(true);
  };

  const portalContainerClasses = useMemo(() => {
    return clsx(
      "fixed inset-0 z-50 flex items-end justify-center bg-black/50",
      "transform transition-all duration-300",
      isAnimating ? "opacity-100" : "opacity-0",
    );
  }, [isAnimating]);

  const portalContentClasses = useMemo(() => {
    return clsx(
      "w-full rounded-t-2xl bg-white",
      "transform transition-all duration-300",
      isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
    );
  }, [isAnimating]);

  const webDropdownClasses = useMemo(() => {
    return clsx(
      "web-dropdown shadow-xxl absolute top-[calc(100%+12px)] -left-4 z-50 mt-1 w-[96px] rounded-lg bg-white",
      "origin-top transform overflow-hidden ring-0 transition-all duration-200 ease-out",
      isWebDropdownAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : "-translate-y-2 scale-95 opacity-0",
    );
  }, [isWebDropdownAnimating]);

  const handlePortalClose = useCallback(() => {
    onClicked(false);
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyMobile = useCallback(() => {
    onSelect?.(time);

    onClicked(false);
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, onSelect]);

  useEffect(() => {
    if (escapePressed && isOpen) {
      onClicked(false);
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escapePressed, isOpen]);

  useEffect(() => {
    if (isOpen && isMobile) {
      setTime(value);

      setShouldRenderPortal(true);
      const tO = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(tO);
    } else if (!isOpen && isMobile) {
      setIsAnimating(false);
      const tO = setTimeout(() => {
        setShouldRenderPortal(false);
      }, 300);
      return () => clearTimeout(tO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isOpen && !isMobile) {
      if (scrollToForm) {
        const inputRect = ref.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      // Web dropdown açılıyor
      setShouldRenderWebDropdown(true);
      // Küçük gecikme ile animasyonu tetikle
      const timer = setTimeout(() => {
        setIsWebDropdownAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen && !isMobile) {
      // Web dropdown kapanıyor
      setIsWebDropdownAnimating(false);
      // Animasyon bitimini bekle, sonra DOM'dan kaldır
      const timer = setTimeout(() => {
        setShouldRenderWebDropdown(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMobile]);

  const handleChangeMobile = useCallback((newVal) => {
    setTime(newVal);
  }, []);

  const handleChangeDesktop = useCallback((newVal) => {
    onSelect(newVal);
    onClicked(false);
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderWheelPickers = useCallback(() => {
    const onValueChange = (val) => handleChangeMobile(val);

    return (
      <WheelPickerWrapper>
        <WheelPicker
          options={timeOptions}
          value={time}
          onValueChange={onValueChange}
          classNames={{
            optionItem: "text-foreground-muted !text-lg md:!text-base",
            highlightWrapper:
              "bg-background-300 rounded-tl-lg rounded-bl-lg !text-lg md:!text-base",
          }}
        />
      </WheelPickerWrapper>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOptions, handleChangeMobile, value]);

  const listRef = useCallback(
    (node) => {
      if (node !== null) {
        if (value) {
          const [selectedSlotElem] =
            document.getElementsByClassName(`selected-time`);

          if (selectedSlotElem) {
            const ulHeight = node.clientHeight;
            const itemTop = selectedSlotElem.offsetTop;
            const itemHeight = selectedSlotElem.clientHeight;

            node.scrollTo({
              top: itemTop - ulHeight / 2 + itemHeight / 2,
              behavior: "smooth",
            });
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, timeOptions],
  );

  const renderWebDropdownContent = useCallback(() => {
    return (
      <ul
        ref={listRef}
        className="scrollbar-default scrollbar-hover-only flex max-h-[256px] flex-col overflow-y-auto py-2 hover:[&_.selected_time]:bg-none"
      >
        {timeOptions.map((timeOption) => (
          <li
            id={`timeSlot_${timeOption.value}_${id}`}
            key={timeOption.value}
            className="w-full"
          >
            <button
              className={clsx(
                "hover:bg-primary-weaker w-full cursor-pointer px-4 py-[6px] text-left font-medium",
                value === timeOption.value
                  ? "selected-time bg-primary-weaker"
                  : "",
              )}
              onClick={() => {
                handleChangeDesktop(timeOption.value);
              }}
            >
              {timeOption.label}
            </button>
          </li>
        ))}
      </ul>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOptions, handleChangeDesktop, value]);

  return (
    <div className="relative flex h-full min-w-[68px] items-end">
      <div ref={ref} className="flex h-full flex-row items-center gap-1">
        <div className="relative h-full">
          <button
            id={id}
            onClick={handleClick}
            className="flex h-full cursor-pointer items-end gap-1 font-semibold"
          >
            <div className="flex items-center gap-6 md:gap-[2px]">
              {value}

              <FontAwesomeIcon
                icon={byPrefixAndName.fak["chevron-down-new"]}
                className="text-foreground-muted !h-4 !w-4"
              />
            </div>
          </button>

          {/* Web dropdown */}
          {!isMobile && shouldRenderWebDropdown && (
            <div className={webDropdownClasses}>
              <ViewportHeightWrapper
                defaultMaxHeight={460}
                bottomPadding={32}
                enabled={viewportHeightWrapperActive}
              >
                {renderWebDropdownContent()}
              </ViewportHeightWrapper>
            </div>
          )}
        </div>
      </div>

      {/* MOBILE PORTAL */}
      {isMobile &&
        shouldRenderPortal &&
        createPortal(
          <div className={portalContainerClasses} onClick={handlePortalClose}>
            <div
              className={portalContentClasses}
              onClick={(e) => e.stopPropagation()}
            >
              {/* header */}
              <div className="border-primary-weak relative flex items-center justify-between border-b py-2">
                <button
                  onClick={handlePortalClose}
                  className="!h-5 !w-5 pl-4 text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["xmark"]}
                    className="!h-5 !w-5"
                  />
                </button>

                <span className="absolute top-1/2 left-1/2 -translate-1/2 text-lg font-semibold">
                  {bottomSheetTitle}
                </span>

                <div className="flex items-center">
                  <button
                    className="text-md px-3 py-2 font-medium text-[#2185f8]"
                    onClick={handleApplyMobile}
                  >
                    {t("common.apply")}
                  </button>
                </div>
              </div>

              {/* içerik */}
              <div className="w-full flex-col py-5">{renderWheelPickers()}</div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
